/**
 * Getting a check-in answer to the server, and insisting a little.
 *
 * A check-in that does not land expires MISSED and costs somebody their
 * availability score. That makes it the one request in this application worth
 * retrying rather than reporting: the person answered, and losing their answer
 * in transit is the system's fault being charged to them.
 *
 * **Nothing else can cover this.** The heartbeat discount exists for exactly
 * that unfairness, but it stamps every 30s and needs a 90s gap before it will
 * call an outage. The failure this guards against is not a short outage -- it
 * is not an outage at all. A 502 from a pooled connection the server has
 * already closed happens while the API is up and answering everything else, so
 * there is no gap for the heartbeat to find and no threshold that would reach
 * it. The retry has to happen here, at the point of failure.
 *
 * Only transport failures are retried. 502, 503 and 504 are the front door
 * failing to reach a server that is running, and a network error is the same
 * thing seen from further out. Everything else is an answer, and two of those
 * answers matter:
 *
 * - **409** means the ping was already answered. After a lost response that is
 *   the *success* case rather than a failure -- the first attempt committed and
 *   only the reply went missing -- so it resolves.
 * - **410** means the grace window has closed. Retrying past it asks the server
 *   to lie about when somebody replied, and it refuses anyway.
 *
 * Bounded by the window rather than by a count, because the window is the thing
 * that actually matters: there is no point spending it on an attempt that will
 * arrive after the deadline it was trying to beat.
 */

/** What the caller's failed request looked like. Kept transport-agnostic so
 *  this is testable without a network or an axios instance. */
export interface DeliveryFailure {
  /** HTTP status, or undefined when the request never got an answer. */
  status?: number;
  /** True when the request failed before reaching the server. */
  networkError?: boolean;
}

export interface DeliveryOptions {
  /** When the grace window closes, as epoch milliseconds. */
  deadline: number;
  /** How long to wait between attempts. */
  delayMs?: number;
  /** Injected for tests. */
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

/** Statuses that mean "the server is there, the path to it is not". */
const RETRYABLE = new Set([502, 503, 504]);

/** Already answered. A success wearing a failure's status code. */
const ALREADY_ANSWERED = 409;

export function isRetryable(failure: DeliveryFailure): boolean {
  if (failure.status === undefined) return failure.networkError === true;
  return RETRYABLE.has(failure.status);
}

export function isAlreadyAnswered(failure: DeliveryFailure): boolean {
  return failure.status === ALREADY_ANSWERED;
}

/**
 * Run `post` until it lands, the window closes, or it fails for a reason
 * repeating will not fix.
 *
 * `classify` turns whatever the caller's transport threw into a
 * DeliveryFailure. Anything it cannot classify is rethrown untouched.
 */
export async function deliverCheckIn(
  post: () => Promise<void>,
  classify: (error: unknown) => DeliveryFailure | null,
  { deadline, delayMs = 1200, now = Date.now, sleep = defaultSleep }: DeliveryOptions,
): Promise<void> {
  for (;;) {
    try {
      await post();
      return;
    } catch (error) {
      const failure = classify(error);
      if (failure === null) throw error;
      if (isAlreadyAnswered(failure)) return;
      if (!isRetryable(failure)) throw error;
      // Stop rather than spend the window on an attempt that lands after it.
      if (now() + delayMs >= deadline) throw error;
      await sleep(delayMs);
    }
  }
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
